// ActionMonitor — the app's single background-work monitor: a floating button (bottom-left) and
// the "Zadania w tle" drawer. One chronological list of both tracked sources: actions
// (ActionContext — useAsyncAction step ladders, e.g. invoice actions) and processes
// (ProcessContext — useBatchAction / useUploadProcess batches, rendered with ProcessCard so
// retry / fix / the file list keep working).
import React, { useState } from 'react';
import { Badge, Button, Drawer, Progress, Tooltip } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  LoadingOutlined,
  BellOutlined,
  DeleteOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';
import { useActions, type Action } from '@ds/actions/useActions';
import { useProcesses } from '@ds/actions/useProcesses';
import { SEMANTIC_COLORS, LAYOUT_TOKENS } from "@ds/tokens/tokens";
import { TextValue } from "@ds/display/TextValue";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { Box } from "@ds/layout/Box";
import { EmptyState } from "@ds/display/EmptyState";
import { ProcessCard } from './ProcessMonitor';
import { mergeMonitorEntries } from './monitorEntries';

/**
 * One action (step ladder) as a card, framed like ProcessCard
 */
const ActionCard: React.FC<{ action: Action; onRemove: (id: string) => void }> = ({
  action,
  onRemove
}) => {
  const getStatusIcon = () => {
    switch (action.status) {
      case 'pending':
        return <LoadingOutlined spin style={{ color: SEMANTIC_COLORS.info, fontSize: LAYOUT_TOKENS.fontSizeSubheading }} />;
      case 'completed':
        return <CheckCircleOutlined style={{ color: SEMANTIC_COLORS.valid, fontSize: LAYOUT_TOKENS.fontSizeSubheading }} />;
      case 'error':
        return <CloseCircleOutlined style={{ color: SEMANTIC_COLORS.danger, fontSize: LAYOUT_TOKENS.fontSizeSubheading }} />;
      default:
        return null;
    }
  };

  return (
    <Box size="md" border shape="rounded" testId="action-card">
      <Stack gap="xs" fullWidth>
        <Inline gap="sm" align="center">
          {getStatusIcon()}
          <Box grow>
            <TextValue as="div" weight="medium" size="sm">{action.label}</TextValue>
          </Box>
          {action.status !== 'pending' && (
            <Button type="text" size="small" icon={<DeleteOutlined />}
              aria-label="Usuń" data-testid="action-card-remove"
              onClick={() => onRemove(action.id)} />
          )}
        </Inline>

        {/* Description */}
        {action.description && (
          <TextValue tone="muted" size="xs">
            {action.description}
          </TextValue>
        )}

        {/* Show steps progress while pending; show result messages after completion */}
        {action.steps && action.steps.length > 0 && (action.status === 'pending' || action.steps.some(s => s.message && s.message !== 'Completed')) && (
          <Stack gap="xs">
            {action.steps.map(step => (
              <Stack key={step.id} gap="none">
                <Inline gap="xs" wrap>
                  {step.status === 'pending' && <LoadingOutlined spin style={{ fontSize: LAYOUT_TOKENS.fontSizeSmall, color: SEMANTIC_COLORS.info }} />}
                  {step.status === 'success' && <CheckCircleOutlined style={{ fontSize: LAYOUT_TOKENS.fontSizeSmall, color: SEMANTIC_COLORS.valid }} />}
                  {step.status === 'error' && <CloseCircleOutlined style={{ fontSize: LAYOUT_TOKENS.fontSizeSmall, color: SEMANTIC_COLORS.danger }} />}
                  <TextValue tone="muted" size="xs">{step.label}</TextValue>
                  {step.status !== 'pending' && step.message && step.message !== 'Completed' && (
                    <TextValue tone="muted" size="xs">— {step.message}</TextValue>
                  )}
                </Inline>
                {step.progress !== undefined && step.status === 'pending' && (
                  <Progress
                    percent={step.progress}
                    size="small"
                    showInfo={false}
                    style={{ marginLeft: 16, marginTop: 2 }}
                  />
                )}
              </Stack>
            ))}
          </Stack>
        )}

        {/* Error message */}
        {action.error && (
          <TextValue tone="red" size="xs">
            {action.error}
          </TextValue>
        )}

        {/* Result link */}
        {action.resultLink && (
          action.resultLink.onClick ? (
            <Button type="link" size="small" icon={<LinkOutlined />} style={{ padding: 0 }} onClick={action.resultLink.onClick}>
              {action.resultLink.label}
            </Button>
          ) : action.resultLink.url?.startsWith('http') ? (
            <a href={action.resultLink.url} target="_blank" rel="noopener noreferrer">
              <Button type="link" size="small" icon={<LinkOutlined />} style={{ padding: 0 }}>
                {action.resultLink.label}
              </Button>
            </a>
          ) : action.resultLink.url ? (
            <Link to={action.resultLink.url}>
              <Button type="link" size="small" icon={<LinkOutlined />} style={{ padding: 0 }}>
                {action.resultLink.label}
              </Button>
            </Link>
          ) : null
        )}

        {/* Completion time */}
        {action.completedAt && (
          <TextValue tone="muted" size="xs">
            {new Date(action.completedAt).toLocaleTimeString()}
          </TextValue>
        )}
      </Stack>
    </Box>
  );
};

/**
 * Action Monitor Drawer
 */
const ActionMonitor: React.FC<{ visible: boolean; onClose: () => void }> = ({
  visible,
  onClose
}) => {
  const { actions, removeAction, clearCompleted: clearCompletedActions } = useActions();
  const { processes, removeProcess, clearCompleted: clearCompletedProcesses } = useProcesses();

  const entries = mergeMonitorEntries(actions, processes);
  const hasFinished = actions.some(a => a.status !== 'pending') || processes.some(p => p.status !== 'running');
  const clearFinished = () => { clearCompletedActions(); clearCompletedProcesses(); };

  return (
    <Drawer
      title="Zadania w tle"
      placement="right"
      onClose={onClose}
      open={visible}
      width={450}
      extra={
        hasFinished ? (
          <Button
            size="small"
            onClick={clearFinished}
            data-testid="action-monitor-clear"
          >
            Wyczyść ukończone
          </Button>
        ) : null
      }
    >
      {entries.length === 0 ? (
        <EmptyState title="Brak zadań" size="sm" testId="action-monitor-empty" />
      ) : (
        <Stack gap="sm">
          {entries.map(entry => (
            entry.kind === 'action' ? (
              <ActionCard key={entry.key} action={entry.action} onRemove={removeAction} />
            ) : (
              <ProcessCard key={entry.key} process={entry.process} onRemove={removeProcess} />
            )
          ))}
        </Stack>
      )}
    </Drawer>
  );
};

/**
 * Floating Action Button
 */
export const ActionMonitorButton: React.FC<{ onOpenChange?: (open: boolean) => void }> = ({ onOpenChange }) => {
  const [visible, setVisibleState] = useState(false);
  const setVisible = (open: boolean) => {
    setVisibleState(open);
    onOpenChange?.(open);
  };
  const { actions } = useActions();
  const { processes } = useProcesses();

  const runningCount = actions.filter(a => a.status === 'pending').length
    + processes.filter(p => p.status === 'running').length;
  const hasErrors = actions.some(a => a.status === 'error')
    || processes.some(p => p.status === 'completed_with_errors' || p.status === 'stale');

  const isEmpty = actions.length === 0 && processes.length === 0;

  // Only the button hides when nothing is tracked. The drawer stays mounted,
  // so an open drawer whose last entry goes away shows "Brak zadań" instead
  // of vanishing with `visible` still true and popping open on the next entry.
  //
  // The corner pin sits on the container, not on the Button: a fixed Button
  // leaves the Badge wrapper as a 0×0 box in the page flow, and the count
  // bubble (positioned against that wrapper) rendered off-screen.
  return (
    <>
      {!isEmpty && (
        <div
          data-testid="action-monitor"
          style={{ position: 'fixed', bottom: 24, left: 24, zIndex: 2000 }}
        >
          <Tooltip title="Zadania w tle" placement="right">
            <Badge count={runningCount} showZero={false} offset={[-5, 5]} data-testid="action-monitor-badge">
              <Button
                type={hasErrors ? 'primary' : runningCount > 0 ? 'primary' : 'default'}
                danger={hasErrors}
                shape="circle"
                size="large"
                icon={runningCount > 0 ? <LoadingOutlined spin /> : <BellOutlined />}
                onClick={() => setVisible(true)}
                data-testid="action-monitor-button"
                aria-label="Zadania w tle"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
              />
            </Badge>
          </Tooltip>
        </div>
      )}
      <ActionMonitor visible={visible} onClose={() => setVisible(false)} />
    </>
  );
};
